import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { getNotificationData, updateNotificationData } from "../../redux/slices/NotificationAlertSlice";
import { headersType } from "../../models";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NotFound from "../../assets/images/no-forms.png";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import { BreadCrumbs, Button, TableHeader, TableRow, Tabs } from "../../components";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { DATE_TIME_DISPLAY_FORMAT } from "../../utils/constants";

interface NotificationItem {
  name: string;
  triggeredBy: string;
  audience: string;
  maintenanceStartWindow: string;
  maintenanceEndWindow: string;
  active: boolean;
  notificationId: string;
  message: string;
  notificationAlertType: string;
  status: string;
  // add other fields as needed
}

export const headersInitialState: headersType[] = [
  {
    title: "Name",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "name",
    isClickable: false,
  },
  {
    title: "Triggered By",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "triggeredBy",
    isClickable: false,
  },
  {
    title: "audience",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "audience",
    isClickable: false,
  },
  {
    title: "Start Date",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "maintenanceStartWindow",
    isClickable: false,
  },
  {
    title: "End Date",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "maintenanceEndWindow",
    isClickable: false,
  },
  {
    title: "",
    extraClasses: "w-40 pr-0",
    sortKey: "",
    isClickable: false,
  },
];

const NotificationListing = ({ isAdminPage }: { isAdminPage: boolean }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [headers] = useState(headersInitialState);
  const [data, setData] = useState<NotificationItem[] | undefined>(undefined);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!data) {
      const fetchData = async () => {
        const res = await dispatch(getNotificationData());
        const response = res.payload as { data: NotificationItem[] };
        setData(response.data);
      };
      fetchData();
    }
  }, [dispatch, data]);
  const extraClasses = useMemo(() => headers.map(head => head.extraClasses), [headers]);
  const handleToggle = async (notificationId: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    setData(prev => prev?.map(item => (item.notificationId === notificationId ? { ...item, active: newStatus } : item)));
    const updatedRow = data?.find(item => item.notificationId === notificationId);
    if (!updatedRow) return;
    const { notificationAlertType, ...rest } = updatedRow;

    const myData = {
      ...rest,
      type: notificationAlertType,
      active: newStatus, // override with new status
    };

    const res = await dispatch(updateNotificationData(myData));
    if (res.meta.requestStatus === "fulfilled") {
      const payload = res.payload as { data: any; status: number };
      if (payload.status === 200) {
        toast?.openToast("Notification updated Successfully", 2000);
      } else {
        setData(prev => prev?.map(item => (item.notificationId === notificationId ? { ...item, active: currentStatus } : item)));
        toast?.errorToast(payload?.data?.message ? payload?.data?.message : "Something went wrong");
      }
    }
  };

  const filteredData = useMemo(() => {
    if (activeIndex === 1) {
      return data?.filter(item => item.audience === "OFFICE");
    } else if (activeIndex === 2) {
      return data?.filter(item => item.audience === "PATIENT");
    }
    return data;
  }, [activeIndex, data]);

  const changeServiceTab = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <>
      <main>
        <div className="container mx-auto p-8">
          {!isAdminPage ? <OfficeSectionsHeader showMeta={true} /> : <BreadCrumbs items={[{ key: "#", title: "Notifications" }]} />}
          <div className="block lg:flex justify-between items-center mb-6">
            <div className="mb-5 lg:mb-0 w-[-webkit-fill-available]">
              <h2 className="heading uppercase">Notifications</h2>
            </div>
            <div className="block md:flex lg:justify-end flex-wrap w-full gap-2 items-center">
              <Button className="w-auto" text="Create Notification" onClick={() => navigate("/create-notification")} />
            </div>
          </div>
          <Tabs
            components={[]}
            options={["ALL", "Offices", "Patient"]}
            activeTab={activeIndex}
            onClickTab={changeServiceTab}
            fromOffice={false}
            showBottomBorder={false}
          />
          <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
            {headers.map((head, index: number) => (
              <TableHeader
                title={head.title}
                index={index}
                key={index}
                sortIcon={null}
                isClickable={head.isClickable}
                handleSort={() => null}
                extraClassesHeader={extraClasses[index]}
                isCheckbox={head?.isCheckbox}
                selectAll={() => null}
              />
            ))}
          </div>
          {filteredData?.length ? (
            <>
              {filteredData?.map((rowData: any, index: any) => {
                const sortedData = {
                  name: rowData.name,
                  triggeredBy: rowData?.triggeredBy,
                  audience: rowData?.audience,
                  maintenanceStartWindow: moment(rowData.maintenanceStartWindow).format(DATE_TIME_DISPLAY_FORMAT),
                  maintenanceEndWindow: moment(rowData.maintenanceEndWindow).format(DATE_TIME_DISPLAY_FORMAT),
                  status: (
                    <div className="flex gap-3 justify-end">
                      <>
                        <label className="inline-flex relative items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={rowData.active}
                            onChange={() => handleToggle(rowData.notificationId, rowData.active)}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer dark:bg-gray-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                      </>
                    </div>
                  ),
                };
                return (
                  <div key={index}>
                    <TableRow
                      data={Object.values(sortedData)}
                      key={index}
                      id={index.toString()}
                      extraClassesRow={extraClasses}
                      handleCloseTooltip={() => null}
                      handleOpenTooltip={() => null}
                      tooltipVisible={false}
                      onRowClick={() => null}
                    />
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <DataNotFound image={NotFound} text="No Data found" />
            </>
          )}
          {/* <Pagination
          colSpan={10}
          count={templateList?.content ? templateList.totalElements : 0}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={sort.pageNumber}
          rowsPerPage={sort.rowsPerPage}
        /> */}
        </div>
      </main>
    </>
  );
};
export default NotificationListing;
